import { HttpEvent, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject,
  Input, OnInit, Output, ViewChild
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { faDownload, faEye, faFolder, faHome, faUpload } from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';


import {
  EMPTY_GUID,
  FILE_MANAGER_SERVICE_TOKEN,
  FileExtensionCategory,
  FileManagerDataService,
  FilesAndFolders,
  StaticFile
} from '../services/file-manager.service';
import { IModalService, MODAL_SERVICE_TOKEN, ModalModel } from '../services/IModalService';

import { CreateFolderComponent } from './create-folder/create-folder.component';

@UntilDestroy()
@Component({
    selector: 'kh-upload-files-listing-modal',
    styleUrls: ['./upload-files-listing-modal.component.less'],
    templateUrl: './upload-files-listing-modal.component.html',
    standalone: false
})
export class UploadFilesListingModalComponent implements OnInit {
  @Input() public imagePath = '';
  @Input() public track = 'ui-tinymce-1';
  @Input() public lang?: string;
  @Input() public allowFolders = true;
  @ViewChild('link', { read: ElementRef }) public link!: ElementRef<HTMLInputElement>;

  public fileExtensionCategory = FileExtensionCategory;

  @Output() public linkSet = new EventEmitter<string>();

  public source$: Observable<FilesAndFolders> | undefined;
  public sourceFolder = '';
  public percentComplete = 0;
  public uploadForm: FormGroup | null = null;
  public trashIcon = faTrashAlt;
  public downloadIcon = faDownload;
  public eyeIcon = faEye;
  public homeIcon = faHome;
  public uploadIcon = faUpload;
  public folderIcon = faFolder;
  public isUploading = false;
  public emptyGuid = EMPTY_GUID;

  public constructor(
    private readonly bsModalRef: BsModalRef,
    @Inject(FILE_MANAGER_SERVICE_TOKEN) private readonly fileManagerDataService: FileManagerDataService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly formBuilder: FormBuilder,
    @Inject(MODAL_SERVICE_TOKEN) private readonly modalService: IModalService
  ) {
  }

  public ngOnInit(): void {
    this.sourceFolder = this.imagePath;
    this.getSources();
    this.createUploadForm();
  }

  public closeModal(): void {
    this.linkSet.emit(this.link.nativeElement.value);
    this.bsModalRef.hide();
  }

  public selectFiles(filesEvent: Event): void {
    const target = filesEvent.target as HTMLInputElement;
    const files = target.files;
    const filesFormArray = this.uploadForm?.get('files') as FormArray;
    filesFormArray.clear();
    if (files) {
      let fileFormControl: FormControl;
      for (let i = 0; i < files.length; i++) {
        fileFormControl = this.createFileFormControl(files[i]);
        filesFormArray.push(fileFormControl);
      }
    }
  }

  public uploadFiles(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const files = this.uploadForm?.value.files as FileList;
    this.fileManagerDataService.upload(this.sourceFolder, files)
      .pipe(
        tap((event: HttpEvent<unknown>) => {
          if (event && event.type === HttpEventType.UploadProgress && event.total) {
            this.percentComplete = event.total > 0 ? event.loaded / event.total * 100 : 0;
            this.changeDetectorRef.markForCheck();
          }
        }),
        filter((event: HttpEvent<unknown>) => event.type === HttpEventType.ResponseHeader),
        tap(() => {
          this.createUploadForm();
          this.changeFolder(this.sourceFolder);
          this.changeDetectorRef.detectChanges();
        }),
        catchError((httpError: unknown) => {
          alert('Error uploading');
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return throwError(() => httpError);
        })
      )
      .subscribe();
  }

  public createUploadForm(): void {
    this.uploadForm = this.formBuilder.group({
      files: this.formBuilder.array([]),
      folder: this.formBuilder.control('')
    });
  }

  public deleteFolder(folder: string): void {
    this.fileManagerDataService.deleteFolder(folder)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.changeFolder('');
      });
  }

  public deleteFile(fileId: string): void {
    this.fileManagerDataService.deleteFile(fileId)
      .pipe(untilDestroyed(this))
      .subscribe(() => { this.getSources(); });
  }

  public changeFolder(folder: string, reload = true): void {
    this.isUploading = false;
    if (folder) {
      this.sourceFolder = folder;
    } else {
      this.sourceFolder = this.sourceFolder.split('/')[0];
    }
    if (reload) {
      this.getSources();
    } else {
      this.source$ = undefined;
    }
  }

  public linkSelected(file: StaticFile) {
    this.link.nativeElement.value = file.fullPath;
    this.closeModal();
  }

  public downloadFile(file: StaticFile) {
    const downloadLink = document.createElement('a');
    downloadLink.target = '_blank';
    downloadLink.download = file.name;
    downloadLink.href = file.fullPath;
    downloadLink.click();
    downloadLink.remove();
  }

  public getExtension(fileName: string): string {
    return fileName.split('.')[1].toLowerCase();
  }

  public getBreadcrumbs(path: string): string[] {
    const splitPath = path.split('/');
    if (splitPath.length > 1) {
      splitPath.shift();

      return splitPath.filter(x => x !== '');
    }

    return [];
  }

  public getFragmentToIndex(index: number): string {
    return this.sourceFolder
      .split('/')
      .slice(0, index + 2)
      .join('/');
  }

  public toggleUploadForm(): void {
    this.isUploading = !this.isUploading;
  }

  public getFileControls(): FormControl[] {
    if (this.uploadForm) {
      return (this.uploadForm?.controls.files as FormArray).controls as FormControl[];
    }

    return [];
  }

  private getSources(): void {
    this.source$ = undefined;
    this.source$ = this.fileManagerDataService.getSources(this.sourceFolder);
  }

  private createFileFormControl(file: File): FormControl {
    return this.formBuilder.control(file);
  }

  public createFolder(): void {
    const modalOptions: ModalModel = {
      className: 'class-os-pro-paywall-modal',
      data: {
        createFolderCallback: (folderName: string) => {
          if (!folderName) { return; }
          const newPath = this.sourceFolder + '/' + folderName.trim().toLowerCase();
          this.changeFolder(newPath, false);
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.modalService.displayCustomModal(
      CreateFolderComponent,
      modalOptions
    );
  }
}
